// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/effects-seat.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// effects-seat.ts — the app's own REDUCE-EFFECTS switch, and the one place
// that answers "may this style animate / refract / bloom right now?".
//
// Three independent lanes say no, and they are OR-ed, never averaged:
//
//   1. `prefers-reduced-motion: reduce`        — the viewer's OS
//   2. `prefers-reduced-transparency: reduce`  — the viewer's OS
//   3. the app's own switch                    — this module
//
// The OS lanes are already honoured in CSS (`@media` arms beside every
// effect) and in style-axis.ts (`effectiveGrade` refuses the canvas ground
// outright). What they cannot express is "this machine is fine but this
// window is on a projector / a remote desktop / a battery", which is what
// lane 3 is for. It stamps `data-reduce="1"` on <html> so the CSS arms are
// literally the same rules, and it is readable from JS so a tracker can go
// INERT rather than writing custom properties CSS then ignores (R-H3: under
// any reduce lane, hovering a card must leave ZERO inline highlight props).
//
// Per-browser, deliberately: this is a rendering-comfort setting for the
// machine in front of you, not an account preference that should follow you
// onto a different screen. It rides localStorage, and a storage refusal
// (private windows) degrades to "not reduced", which is the shipped look.

/** The stored switch. */
export const REDUCE_EFFECTS_KEY = 'helm-reduce-effects';
/** The attribute the CSS arms match. */
export const REDUCE_ATTR = 'data-reduce';

type Listener = () => void;
const listeners = new Set<Listener>();
let bound = false;

function mq(query: string): MediaQueryList | null {
  if (typeof matchMedia !== 'function') return null;
  try {
    return matchMedia(query);
  } catch {
    return null;
  }
}

/** OS lane 1. */
export function prefersReducedMotion(): boolean {
  return mq('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

/** OS lane 2. */
export function prefersReducedTransparency(): boolean {
  return mq('(prefers-reduced-transparency: reduce)')?.matches ?? false;
}

/** Lane 3 — the app's own switch, as stored. */
export function reduceEffectsSeat(): boolean {
  try {
    return localStorage.getItem(REDUCE_EFFECTS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * THE question. True when ANY lane says reduce.
 *
 * Every effect module asks this and nothing else — so "the CSS hid it but
 * the JS kept writing props" cannot happen, and a fourth lane (if one is
 * ever added) reaches every effect at once.
 */
export function effectsReduced(): boolean {
  return reduceEffectsSeat() || prefersReducedMotion() || prefersReducedTransparency();
}

/** Stamp the attribute from the stored switch. Idempotent. */
function stamp(): void {
  if (typeof document === 'undefined') return;
  const on = reduceEffectsSeat();
  if (on) document.documentElement.setAttribute(REDUCE_ATTR, '1');
  else document.documentElement.removeAttribute(REDUCE_ATTR);
}

/** Flip the app's own switch: persist, stamp, notify. */
export function setReduceEffects(on: boolean): void {
  try {
    if (on) localStorage.setItem(REDUCE_EFFECTS_KEY, '1');
    else localStorage.removeItem(REDUCE_EFFECTS_KEY);
  } catch {
    // storage unavailable — the stamp below still applies for this session
  }
  stamp();
  for (const fn of listeners) fn();
}

/** Subscribe to any lane changing (the switch, or either OS query). */
export function onEffectsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Boot: stamp the attribute and watch BOTH OS queries live, so toggling the
 * setting at the OS level takes effect without a reload. Returns the
 * resolved state so the boot line can name it — an unnamed reduce lane is
 * how "the animation is off for some people" becomes unfalsifiable.
 */
export function bootEffectsSeat(): { reduced: boolean; seat: boolean; motion: boolean; transparency: boolean } {
  stamp();
  if (!bound) {
    for (const q of ['(prefers-reduced-motion: reduce)', '(prefers-reduced-transparency: reduce)']) {
      const m = mq(q);
      m?.addEventListener?.('change', () => {
        for (const fn of listeners) fn();
      });
    }
    bound = true;
  }
  return {
    reduced: effectsReduced(),
    seat: reduceEffectsSeat(),
    motion: prefersReducedMotion(),
    transparency: prefersReducedTransparency(),
  };
}

/** Test seam — drop every subscriber between gate arms. */
export function resetEffectsSeat(): void {
  listeners.clear();
  bound = false;
}
